/**
 * The frozen strip (SA-461, sweep spec §7.1, D10, D11): on the record lane
 * the page's identity, six facts, actions and "now" line pin under the app
 * bar while the record scrolls. Rides `StickyPageHeader bleedX` inside the
 * detail frame. Reads what the band and the cards already cache — the
 * process read, the quote versions, the source — so nothing is fetched
 * twice; the words come from the pure `recordStripModel`. The actions are
 * the commerce ladder and Cancel (ruling R4) under the accessible names the
 * page has always used. No Edit since SA-467 (R1 (A)): the intake re-mirrors
 * the customer name, phone and email onto the order, so the order modal would
 * edit what the next intake save overwrites — the Intake section's
 * `Edit intake` is the lane's one edit. One Start control lives in
 * the band (R2); a failed process read prints no now line at all (the band
 * shows the error).
 */
import { useId } from 'react'
import { Link } from 'react-router-dom'
import { Badge, Button, Tip } from '@/components/ui'
import { buttonVariants } from '@/components/ui/basis/button'
import { StickyPageHeader } from '@/components/layout/StickyPageHeader'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import { useAirportLookup } from '@/hooks/useAirports'
import { useNow } from '@/hooks/useNow'
import { useRecordProcess } from '@/hooks/useProcess'
import { useQuoteVersions, useRecordSource } from '@/hooks/useRecordExtras'
import type { Order } from '@/types/commerce'
import { cn } from '@/utils'
import { noConversationWord } from './birthText'
import { RESPONSE_CHIP_TIP, responseWaitingWords, UPDATE_CHIP_TIP, updateWaitingWords } from './customerUpdateText'
import { COMPARE, duplicateChipText, duplicateChipTitle } from './duplicateText'
import { firstQuoteState } from './firstQuote'
import { FirstQuoteDuration } from './FirstQuoteDuration'
import { AFTER_REQUEST, FIRST_QUOTE, NO_QUOTE_YET, SINCE_REQUEST } from './firstQuoteText'
import { ladderActions } from './ladderActions'
import { useOpenPanel } from './panelLanding'
import { intakeValue, INTAKE_SLOT } from './intakeSnapshot'
import { PlaceStations } from './PlaceStations'
import { stripFacts, stripNow, stripTitleParts } from './recordStripModel'
import { statusSummary } from './sectionSummaries'
import { sourceLabel, sourceRefOf, useSourceHref } from './sourceText'
import { tourAnchor } from '@/components/tour/tourAnchor'

export interface RecordStripProps {
  order: Order
  noun: { singular: string; plural: string }
  showCommerce: boolean
  busy: boolean
  statusError?: string
  assigneeNames?: Record<string, string>
  onBack: () => void
  onStatusAction: (status: string) => void
}

const Sep = () => <span className="text-muted-foreground"> · </span>

export function RecordStrip({ order, noun, showCommerce, busy, statusError, assigneeNames, onBack, onStatusAction }: RecordStripProps) {
  const openPanel = useOpenPanel()
  const duplicateId = useId()
  const zone = useDefaultTimezone()
  const openQueue = () => openPanel('queue')
  const openQuotes = () => openPanel('quotes')
  const openIntake = () => openPanel('intake')
  const record = useRecordProcess(order.id)
  const quotes = useQuoteVersions(order.id)
  const source = useRecordSource(order.id)
  const threadHref = useSourceHref(sourceRefOf(source.data))
  const singular = noun.singular.toLowerCase()
  const facts = stripFacts(order, { assigneeNames, versions: quotes.data?.versions })
  const parts = stripTitleParts(order)
  const { byCode } = useAirportLookup([...parts.originCodes, ...parts.destinationCodes])
  // SA-1204: one minute clock for the now line and the first-quote clause.
  const clock = useNow(60_000)
  const now = record.isError ? null : stripNow(record.data, clock, zone)
  const firstQuote = firstQuoteState(order.firstQuote, order, clock)
  const status = order.commercialStatus ?? null
  const editable = !['delivered', 'cancelled'].includes(order.status)

  return (
    <StickyPageHeader bleedX>
      <div data-testid="record-strip" {...tourAnchor('records.record.strip')} className="border-b border-border px-4 pb-2.5 lg:px-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <Button variant="ghost" size="sm" aria-label={`Back to ${noun.plural.toLowerCase()}`} onClick={onBack} className="px-1.5 text-muted-foreground">
            ← {noun.plural}
          </Button>
          <h2 className="font-mono text-[17px] font-semibold tracking-tight text-foreground">{order.orderNumber}</h2>
          {order.customerUpdate && order.customerUpdate.waiting > 0 && (
            // SA-1029 (spec §D22): the stored state, never a fetch; replace, never push (SA-461).
            <Tip text={UPDATE_CHIP_TIP}>
              <button type="button" onClick={openIntake} className="rounded-full" data-testid="record-strip-update">
                <Badge variant="warning-soft" dot="bg-warning" className="gap-1.5">{updateWaitingWords(order.customerUpdate.waiting)}</Badge>
              </button>
            </Tip>
          )}
          {order.customerUpdate && order.customerUpdate.responses > 0 && (
            // SA-1047 (spec §D7): the customer's answer to the quote; opens Quote versions.
            <Tip text={RESPONSE_CHIP_TIP}>
              <button type="button" onClick={openQuotes} className="rounded-full" data-testid="record-strip-response">
                <Badge variant="warning-soft" dot="bg-warning" className="gap-1.5">{responseWaitingWords(order.customerUpdate.responses)}</Badge>
              </button>
            </Tip>
          )}
          {order.duplicate && (
            // SA-1033 (Q5), SA-1077: the stored fact, never a fetch; Compare only, the Queue section focused.
            <span className="inline-flex items-center gap-1.5" data-testid="record-strip-duplicate">
              <Tip text={duplicateChipTitle({ ofNumber: order.duplicate.recordNumber, score: order.duplicate.score })}>
                <Badge id={duplicateId} variant="warning-soft" dot="bg-warning" tabIndex={0}>{duplicateChipText({ ofNumber: order.duplicate.recordNumber, score: order.duplicate.score })}</Badge>
              </Tip>
              {/* SA-1418 (A5): a screen reader hears "Compare", then the chip's words. */}
              <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs" aria-describedby={duplicateId} onClick={openQueue}>{COMPARE}</Button>
            </span>
          )}
          {parts.origin && parts.destination ? (
            <span data-testid="record-strip-title" className="inline-flex flex-wrap items-center gap-1.5 text-sm font-medium text-foreground">
              <PlaceStations value={intakeValue(order, INTAKE_SLOT.origin)} airports={byCode} />
              <span>{' → '}</span>
              <PlaceStations value={intakeValue(order, INTAKE_SLOT.destination)} airports={byCode} />
              {parts.cargoSize && <span>{` · ${parts.cargoSize}`}</span>}
            </span>
          ) : parts.fallback && (
            <span data-testid="record-strip-title" className="text-sm font-medium text-foreground">{parts.fallback}</span>
          )}
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {threadHref && source.data && (
              <Link to={threadHref} className={buttonVariants({ variant: 'outline', size: 'sm' })}>{sourceLabel(source.data.kind)}</Link>
            )}
            {/* SA-469 (spec §3): a record born without a conversation says how it was entered. */}
            {source.data?.kind === 'none' && (
              <span data-testid="record-strip-born" className="text-xs text-muted-foreground">{noConversationWord(order)}</span>
            )}
            {/* E4 (spec D26): every move INTO the commerce ladder is commerce — `ladderActions` is the one rule. */}
            {ladderActions(order, showCommerce).map((a) => (
              <Button key={a.status} size="sm" onClick={() => onStatusAction(a.status)} disabled={busy}>{a.label}</Button>
            ))}
            {editable && (
              <Button variant="ghost" size="sm" aria-label={`Cancel ${noun.singular}`} onClick={() => onStatusAction('cancelled')} disabled={busy}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive">
                Cancel
              </Button>
            )}
          </div>
        </div>
        {statusError && <p className="mt-1 text-right text-sm text-destructive">{statusError}</p>}

        <dl className="mt-2 flex flex-wrap gap-y-1.5">
          {facts.map((f) => (
            <div key={f.label} className="mr-3.5 flex min-w-0 flex-col gap-px border-r border-border pr-3.5 last:mr-0 last:border-r-0 last:pr-0">
              <dt className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[.06em] text-muted-foreground">{f.label}</dt>
              <dd title={f.title ?? f.value} className={cn('max-w-72 truncate text-[13px] font-medium', f.muted ? 'text-muted-foreground' : 'text-foreground')}>
                {f.value}
                {f.note && <span className="font-normal text-muted-foreground"> · {f.note}</span>}
              </dd>
            </div>
          ))}
        </dl>

        {now && now.kind !== 'loading' && (
          <div data-testid="record-strip-now" className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1.5 rounded-md bg-muted px-2.5 py-1.5 text-[12.5px] text-foreground">
            {status && (
              <Tip text={statusSummary(status)}>
                <span tabIndex={0} className="inline-flex items-center gap-1.5 font-medium">
                  <span aria-hidden="true" className="size-2 rounded-full bg-current" />
                  {status.label}
                </span>
              </Tip>
            )}
            {status && <Sep />}
            {now.kind === 'running' && now.step === null && <span>Process running</span>}
            {now.kind === 'running' && now.step !== null && (
              <>
                <span>Current step <b className="font-semibold">{now.step}</b></span>
                {now.seat && <><Sep /><span>waiting on <b className="font-semibold">{now.seat}</b></span></>}
                {now.due && <><Sep /><span>due <b className="font-semibold">{now.due}</b></span></>}
                {now.overdue && <Badge variant="destructive">overdue</Badge>}
              </>
            )}
            {now.kind === 'closed' && (
              <span><b className="font-semibold">Closed</b>{now.at && <> · {now.at}</>}{now.outcome && <> · {now.outcome}</>}</span>
            )}
            {now.kind === 'none' && <span>No process on this {singular}</span>}
            {firstQuote && (
              <>
                <Sep />
                {firstQuote.kind === 'waiting'
                  ? <span>{NO_QUOTE_YET} · <FirstQuoteDuration {...firstQuote} /> {SINCE_REQUEST}</span>
                  : <span>{FIRST_QUOTE} <FirstQuoteDuration {...firstQuote} /> {AFTER_REQUEST}</span>}
              </>
            )}
          </div>
        )}
      </div>
    </StickyPageHeader>
  )
}
